import {
  Box,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text as ChakraText,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "../../components/Card/Card";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { FaAngleRight, FaArrowLeft } from "react-icons/fa";
import { saveAs } from "file-saver";
import handlePrintReport from "../Reports/ImageSummaryReport";

// Custom AG Grid styles
const agGridStyles = `
  .custom-ag-grid.ag-theme-alpine .ag-root-wrapper {
    border-radius: 12px !important;
    overflow: hidden !important;
    border: 1px solid #e2e8f0 !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-header {
    background-color: #1a365d !important;
    border-top-left-radius: 12px !important;
    border-top-right-radius: 12px !important;
    height: 70px !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-header-row {
    height: 70px !important;
    min-height: 70px !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-header-cell {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    color: #ffffff !important;
    padding: 0 16px !important;
    border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-header-cell:last-child {
    border-right: none !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-row {
    height: 64px !important;
    min-height: 64px !important;
    border-bottom: 1px solid #e2e8f0 !important;
    width: 100% !important;
    text-align: left !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-row-even {
    background-color: #f8fafc !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-row-odd {
    background-color: #ffffff !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-cell {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    height: 64px !important;
    padding: 0 8px !important;
    font-size: 16px !important;
    color: #1a202c !important;
    font-weight: 500 !important;
    text-align: left !important;
    white-space: normal !important;
    word-break: break-word !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-cell.ag-numeric-cell {
    justify-content: center !important;
    text-align: center !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-paging-panel {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    padding: 12px !important;
    background-color: #f7fafc !important;
    border-top: 1px solid #e2e8f0 !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-paging-page-summary-panel,
  .custom-ag-grid.ag-theme-alpine .ag-paging-button {
    font-size: 12px !important;
    color: #2d3748 !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-paging-button:hover {
    background-color: #e2e8f0 !important;
  }
`;

export default function InventoryDashboard() {
  const textColor = useColorModeValue("gray.700", "white");
  const history = useHistory();
  const location = useLocation();
  const [detailViewData, setDetailViewData] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const gridRef = useRef(null);

  // Get runId from URL query parameters
  const query = new URLSearchParams(location.search);
  const runId = query.get("runId") || "1";

  // Fetch table data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BATCH_TRANSACTIONS.GROUPED}?runId=${encodeURIComponent(runId)}`
        );
        const apiData = Array.isArray(response.data) ? response.data : [response.data];

        const transformedData = await Promise.all(
          apiData.map(async (item, index) => {
            const productId = item.productTransaction?.product?.productId ?? "";
            const companyName =
              item.productTransaction?.product?.productCompany?.productCompanyName ??
              "Unknown";
            const brandName =
              item.productTransaction?.product?.productBrand?.productBrandName ??
              "Unknown";

            let additionalData = [];
            try {
              const encodedUrl = encodeURI(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BATCH_TRANSACTIONS.GROUPED_BY_PRODUCT.replace(
                  ":runId",
                  runId
                )
                  .replace(":productId", productId)
                  .replace(":companyName", companyName)
                  .replace(":brandName", brandName)}`
              );
              const secondResponse = await apiClient.get(encodedUrl);
              additionalData = Array.isArray(secondResponse.data)
                ? secondResponse.data
                : [secondResponse.data];
            } catch (error) {
              console.error(
                `Error fetching additional data for ${companyName}/${brandName}: ${error.message}`
              );
            }

            return {
              rowIndex: index,
              sNo: item.sno ?? index + 1,
              company: companyName,
              brand: brandName,
              type: item.productTransaction?.product?.packType ?? "Unknown",
              size: item.productTransaction?.product?.packSize ?? "Unknown",
              productCount: item.productTransaction?.productCount ?? 0,
              additionalData: additionalData.map((data) => ({
                file: data.batchWiseFileTransactions?.fileName ?? "N/A",
                company:
                  data.batchWiseFileTransactions?.product?.productCompany
                    ?.productCompanyName ?? companyName,
                brand:
                  data.batchWiseFileTransactions?.product?.productBrand
                    ?.productBrandName ?? brandName,
                type: data.batchWiseFileTransactions?.product?.packType ?? "Unknown",
                size: data.batchWiseFileTransactions?.product?.packSize ?? "Unknown",
                count: data.batchWiseFileTransactions?.productCount ?? 0,
              })),
            };
          })
        );

        setDetailViewData(transformedData);
      } catch (error) {
        console.error("Error fetching data:", error.message);
        setError("Failed to load data. Please check the API or network.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [runId]);

  const handleViewDetails = (rowData) => {
    setSelectedRowData(rowData);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRowData(null);
  };

  const handleBackToDashboard = () => {
    history.push("/admin/dashboard");
  };

  const handleExportData = () => {
    const headers = [
      "S.No",
      "Company",
      "Brand",
      "Type",
      "Size",
      "Product Count",
      "Nested File",
      "Nested Company",
      "Nested Brand",
      "Nested Pack Type",
      "Nested Pack Size",
      "Nested Count",
    ];
    const rows = detailViewData.flatMap((row) =>
      row.additionalData.length > 0
        ? row.additionalData.map((nested) =>
            [
              row.sNo,
              row.company,
              row.brand,
              row.type,
              row.size,
              row.productCount,
              nested.file || "",
              nested.company || "",
              nested.brand || "",
              nested.type || "",
              nested.size || "",
              nested.count || "",
            ]
              .map((value) => `"${value}"`)
              .join(",")
          )
        : [
            [
              row.sNo,
              row.company,
              row.brand,
              row.type,
              row.size,
              row.productCount,
              "",
              "",
              "",
              "",
              "",
              "",
            ]
              .map((value) => `"${value}"`)
              .join(","),
          ]
    );
    const csvContent = `${headers.join(",")}\n${rows.join("\n")}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `abstract_view.csv`);
  };

  // Main table column definitions
  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        field: "expand",
        width: 30,
        cellRenderer: (params) => {
          if (params.data.additionalData?.length > 0) {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <div
                  onClick={() => handleViewDetails(params.data)}
                  style={{ cursor: "pointer" }}
                >
                  <FaAngleRight size={20} />
                </div>
              </div>
            );
          }
          return null;
        },
        sortable: false,
        filter: false,
      },
      {
        headerName: "S.No",
        field: "sNo",
        sortable: true,
        filter: true,
        width: 100,
      },
      {
        headerName: "Company",
        field: "company",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Brand",
        field: "brand",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Type",
        field: "type",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Size",
        field: "size",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Product Count",
        field: "productCount",
        sortable: true,
        filter: "agNumberColumnFilter",
        cellClass: "ag-numeric-cell",
        valueFormatter: (params) => params.value.toLocaleString(),
      },
    ],
    []
  );

  // Nested table column definitions
  const nestedColumnDefs = useMemo(
    () => [
      { headerName: "File", field: "file", sortable: true, filter: true },
      { headerName: "Company", field: "company", sortable: true, filter: true },
      { headerName: "Brand", field: "brand", sortable: true, filter: true },
      { headerName: "Pack Type", field: "type", sortable: true, filter: true },
      { headerName: "Pack Size", field: "size", sortable: true, filter: true },
      {
        headerName: "Count",
        field: "count",
        sortable: true,
        filter: "agNumberColumnFilter",
        cellClass: "ag-numeric-cell",
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 100,
      resizable: true,
      menuTabs: ["filterMenuTab"],
    }),
    []
  );

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <style>{agGridStyles}</style>
      <Card mb="20px" p="20px">
        <Flex justify="space-between" align="center" mb="20px">
          <Flex alignItems="center" gap="4">
            <Button
              leftIcon={<FaArrowLeft />}
              onClick={handleBackToDashboard}
              size="sm"
              colorScheme="blue"
              variant="outline"
              borderRadius="5px"
            >
              Back
            </Button>
          </Flex>
          <Flex alignItems="center" gap="10px">
            <Button
              onClick={() => handlePrintReport(detailViewData)}
              size="sm"
              colorScheme="gray"
              variant="outline"
              borderRadius="5px"
              fontSize="sm"
              isDisabled={isLoading || error || detailViewData.length === 0}
            >
              Print Report
            </Button>
            <Button
              onClick={handleExportData}
              size="sm"
              bg="black"
              color="white"
              borderRadius="5px"
              fontSize="sm"
              _hover={{ bg: "gray.700" }}
              isDisabled={isLoading || error || detailViewData.length === 0}
            >
              Export Data
            </Button>
          </Flex>
        </Flex>
        <Box
          className="custom-ag-grid ag-theme-alpine"
          height="650px"
          width="100%"
          overflow="hidden"
        >
          <AgGridReact
            ref={gridRef}
            rowData={detailViewData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowHeight={64}
            headerHeight={70}
            pagination={true}
            paginationPageSize={8}
            suppressRowClickSelection={true}
            loadingOverlayComponent={() => (
              <Flex justify="center" align="center" height="100%">
                <ChakraText color="red.500">Loading...</ChakraText>
              </Flex>
            )}
            noRowsOverlayComponent={() => (
              <Flex justify="center" align="center" height="100%">
                <ChakraText color="gray.500">
                  {error || "No data available"}
                </ChakraText>
              </Flex>
            )}
            onGridReady={(params) => {
              if (isLoading) params.api.showLoadingOverlay();
              else if (detailViewData.length === 0 || error)
                params.api.showNoRowsOverlay();
              else params.api.hideOverlay();
            }}
          />
        </Box>
      </Card>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="80vw" maxH="90vh">
          <ModalHeader>Additional Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedRowData && selectedRowData.additionalData?.length > 0 ? (
              <Box
                className="custom-ag-grid ag-theme-alpine"
                height="700px"
                width="100%"
              >
                <AgGridReact
                  rowData={selectedRowData.additionalData}
                  columnDefs={nestedColumnDefs}
                  defaultColDef={defaultColDef}
                  rowHeight={64}
                  headerHeight={70}
                  pagination={true}
                  paginationPageSize={8}
                  suppressRowClickSelection={true}
                  enableCellTextSelection={true}
                  noRowsOverlayComponent={() => (
                    <Flex justify="center" align="center" height="100%">
                      <ChakraText color="gray.500">
                        No additional data available
                      </ChakraText>
                    </Flex>
                  )}
                />
              </Box>
            ) : (
              <ChakraText>No additional data available.</ChakraText>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleCloseModal}
              size="md"
              borderRadius="5px"
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
